import React, { useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle,
} from './ui/dialog';
import { Button } from './ui/button';
import { Label } from './ui/label';
import { Input } from './ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Checkbox } from './ui/checkbox';
import { ImageIcon, Upload } from 'lucide-react';
import api, { UPLOAD_TIMEOUT } from '../lib/api';
import { supabase } from '../lib/supabase';
import { compressImage } from '../lib/imageCompression';
import { BannerPreview } from './BannerCreative';

const MAX_RAW_BYTES = 20 * 1024 * 1024;
const ALLOWED_TYPES = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'];
const DURATIONS = [7, 14, 30, 60, 90];
// Closed CTA set — mirrors banner_service.CTA_KINDS + BannerCreative's CTA_TEXT.
const CTA_OPTIONS = [
  { value: 'learn', key: 'banner.learnMore', fallback: 'Learn more' },
  { value: 'apply', key: 'banner.applyNow', fallback: 'Apply now' },
  { value: 'register', key: 'banner.registerNow', fallback: 'Register now' },
  { value: 'signup', key: 'banner.signUpNow', fallback: 'Sign up now' },
  { value: 'join', key: 'banner.joinNow', fallback: 'Join now' },
];

/**
 * Admin-only platform banner form (spec §5/§6): URL-linked (internal path or https),
 * no job, no credits. Create mode (banner=null → POST /api/admin/banners) and edit
 * mode (banner set → PATCH /api/admin/banners/{id}; duration not editable). Live
 * preview via the shared BannerCreative with the "Featured" gold tag + "Learn more"
 * CTA — exactly how it publishes on the carousel.
 *
 * First use: the PESO Panglao special-recruitment POC banner → /PanglaoPESO.
 */
const AdminBannerFormModal = ({ banner, onClose }) => {
  const { t } = useTranslation();
  const fileInputRef = useRef(null);
  const isEdit = !!banner;

  const [headline, setHeadline] = useState(banner?.headline || '');
  const [subtext, setSubtext] = useState(banner?.subtext || '');
  const [linkUrl, setLinkUrl] = useState(banner?.link_url || '');
  const [sponsorLabel, setSponsorLabel] = useState(banner?.sponsor_label || '');
  const [ctaKind, setCtaKind] = useState(banner?.cta_kind || 'learn');
  const [openNewTab, setOpenNewTab] = useState(!!banner?.open_new_tab);
  const [durationDays, setDurationDays] = useState(30);
  const [imagePosition, setImagePosition] = useState(
    typeof banner?.image_position === 'number' ? banner.image_position : 50
  );
  const [imageFile, setImageFile] = useState(null);
  const [imagePreviewUrl, setImagePreviewUrl] = useState(banner?.image_url || null);
  const [compressing, setCompressing] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const handleFileSelect = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const fileName = file.name?.toLowerCase() || '';
    const hasValidExtension = /\.(jpe?g|png|webp)$/i.test(fileName);
    if (!ALLOWED_TYPES.includes(file.type) && !(file.type === '' && hasValidExtension)) {
      toast.error(t('banner.errorImageType', 'Only JPEG, PNG, or WebP images are allowed.'));
      return;
    }
    if (file.size > MAX_RAW_BYTES) {
      toast.error(t('banner.errorImageSize', 'Image must be smaller than 20MB.'));
      return;
    }
    const reader = new FileReader();
    reader.onloadend = () => setImagePreviewUrl(reader.result);
    reader.readAsDataURL(file);
    setCompressing(true);
    try {
      const compressed = await compressImage(file, 'jobImage');
      setImageFile(compressed);
    } catch {
      setImageFile(file);
    } finally {
      setCompressing(false);
    }
  };

  const canSubmit = headline.trim() && sponsorLabel.trim() && linkUrl.trim()
    && (isEdit || imageFile) && !compressing && !submitting;

  const handleSubmit = async () => {
    if (!canSubmit) return;
    setSubmitting(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        toast.error(t('common.loginRequired', 'Please log in.'));
        return;
      }
      const form = new FormData();
      form.append('headline', headline.trim());
      form.append('sponsor_label', sponsorLabel.trim());
      form.append('link_url', linkUrl.trim());
      form.append('subtext', subtext.trim());
      form.append('image_position', String(imagePosition));
      form.append('cta_kind', ctaKind);
      form.append('open_new_tab', openNewTab ? 'true' : 'false');
      if (!isEdit) form.append('duration_days', String(durationDays));
      if (imageFile) form.append('file', imageFile, imageFile.name || 'banner.jpg');

      const config = {
        headers: { Authorization: `Bearer ${session.access_token}` },
        timeout: UPLOAD_TIMEOUT,
      };
      if (isEdit) {
        await api.patch(`/api/admin/banners/${banner.id}`, form, config);
        toast.success(t('banner.adminEditSuccess', 'Platform banner updated.'));
      } else {
        await api.post('/api/admin/banners', form, config);
        toast.success(t('banner.adminCreateSuccess', 'Platform banner is live on the homepage.'));
      }
      onClose(true);
    } catch (err) {
      const detail = typeof err?.response?.data?.detail === 'string' ? err.response.data.detail : '';
      if (err?.response?.status === 400 && detail.toLowerCase().includes('link')) {
        toast.error(t('banner.errorInvalidLink', 'Invalid destination link. Use an internal path like /PanglaoPESO or a full https:// URL.'));
      } else if (detail) {
        // Surface the backend's actual reason — a generic "failed" hides the fix.
        toast.error(detail);
      } else if (isEdit) {
        toast.error(t('banner.adminEditError', 'Failed to update the platform banner.'));
      } else {
        toast.error(t('banner.adminCreateError', 'Failed to create the platform banner.'));
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open onOpenChange={(next) => { if (!next && !submitting) onClose(false); }}>
      <DialogContent className="sm:max-w-lg max-h-[90vh] max-h-[90dvh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>
            {isEdit
              ? t('banner.adminEditTitle', 'Edit platform banner')
              : t('banner.adminCreateTitle', 'New platform banner')}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-3">
          <div>
            <Label htmlFor="pb-headline">{t('banner.headlineLabel', 'Headline')}</Label>
            <Input id="pb-headline" value={headline} maxLength={80}
              onChange={(e) => setHeadline(e.target.value)} data-testid="pb-headline" />
          </div>
          <div>
            <Label htmlFor="pb-subtext">{t('banner.adminDetailLabel', 'Detail line (optional)')}</Label>
            <Input id="pb-subtext" value={subtext} maxLength={100}
              onChange={(e) => setSubtext(e.target.value)}
              placeholder={t('banner.adminDetailPlaceholder', 'e.g. Sat Jul 19 · Panglao Municipal Hall')} />
          </div>
          <div>
            <Label htmlFor="pb-label">{t('banner.adminLabelLabel', 'Shown as (label)')}</Label>
            <Input id="pb-label" value={sponsorLabel} maxLength={60}
              onChange={(e) => setSponsorLabel(e.target.value)}
              placeholder={t('banner.adminLabelPlaceholder', 'e.g. PESO Panglao')} />
          </div>
          <div>
            <Label htmlFor="pb-link">{t('banner.adminLinkLabel', 'Destination link')}</Label>
            <Input id="pb-link" value={linkUrl} maxLength={500}
              onChange={(e) => setLinkUrl(e.target.value)}
              placeholder="/PanglaoPESO" data-testid="pb-link" />
          </div>

          <div className="flex items-start gap-2 pt-1">
            <Checkbox
              id="pb-new-tab"
              checked={openNewTab}
              onCheckedChange={(checked) => setOpenNewTab(checked === true)}
              data-testid="pb-new-tab"
            />
            <Label htmlFor="pb-new-tab" className="text-sm font-normal cursor-pointer leading-tight">
              {t('banner.openNewTabLabel', 'Open link in a new tab (leave off for internal pages)')}
            </Label>
          </div>

          <div>
            <Label>{t('banner.ctaLabel', 'Button text')}</Label>
            <Select value={ctaKind} onValueChange={setCtaKind}>
              <SelectTrigger data-testid="pb-cta"><SelectValue /></SelectTrigger>
              <SelectContent>
                {CTA_OPTIONS.map((o) => (
                  <SelectItem key={o.value} value={o.value}>{t(o.key, o.fallback)}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {!isEdit && (
            <div>
              <Label>{t('banner.adminDurationLabel', 'Duration')}</Label>
              <Select value={String(durationDays)} onValueChange={(v) => setDurationDays(parseInt(v, 10))}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {DURATIONS.map((d) => (
                    <SelectItem key={d} value={String(d)}>
                      {t('banner.durationDays', { count: d, defaultValue: '{{count}} days' })}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          {imagePreviewUrl ? (
            <div className="space-y-2">
              <p className="text-xs font-medium text-muted-foreground">
                {t('banner.livePreviewLabel', 'Live preview — exactly what publishes')}
              </p>
              <div>
                <p className="text-[10px] text-muted-foreground mb-1">{t('banner.previewDesktop', 'Desktop')}</p>
                <BannerPreview
                  mode="desktop"
                  imageUrl={imagePreviewUrl}
                  imagePosition={imagePosition}
                  headline={headline}
                  detail={subtext}
                  label={sponsorLabel}
                  tag="featured"
                  cta={ctaKind}
                  showReport={false}
                />
              </div>
              <div>
                <p className="text-[10px] text-muted-foreground mb-1">{t('banner.previewMobile', 'Mobile')}</p>
                <BannerPreview
                  mode="mobile"
                  className="w-3/5"
                  imageUrl={imagePreviewUrl}
                  imagePosition={imagePosition}
                  headline={headline}
                  detail={subtext}
                  label={sponsorLabel}
                  tag="featured"
                  cta={ctaKind}
                  showReport={false}
                />
              </div>
            </div>
          ) : (
            <div className="relative w-full aspect-[4/1] rounded-lg overflow-hidden border-2 border-border bg-muted">
              <button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                className="flex h-full w-full flex-col items-center justify-center gap-1 text-muted-foreground hover:text-foreground transition-colors"
              >
                <ImageIcon size={28} />
                <span className="text-xs">{t('banner.clickToUpload', 'Click to upload an image')}</span>
              </button>
            </div>
          )}

          <input
            ref={fileInputRef}
            type="file"
            accept="image/jpeg,image/jpg,image/png,image/webp"
            onChange={handleFileSelect}
            className="hidden"
          />
          <Button type="button" variant="outline" onClick={() => fileInputRef.current?.click()} disabled={compressing} className="w-full">
            <Upload className="mr-2 h-4 w-4" />
            {compressing
              ? t('banner.compressing', 'Processing image...')
              : imagePreviewUrl
                ? t('banner.changeImage', 'Change image')
                : t('banner.chooseImage', 'Choose image')}
          </Button>

          {imagePreviewUrl && (
            <div className="space-y-1">
              <Label className="text-xs">{t('banner.adjustFocus', 'Adjust focal point')}</Label>
              <input
                type="range"
                min="0"
                max="100"
                value={imagePosition}
                onChange={(e) => setImagePosition(parseInt(e.target.value, 10))}
                className="w-full h-2 bg-gray-200 rounded-lg cursor-pointer accent-primary"
              />
            </div>
          )}
        </div>

        <DialogFooter className="gap-2">
          <Button type="button" variant="outline" onClick={() => onClose(false)} disabled={submitting}>
            {t('common.cancel', 'Cancel')}
          </Button>
          <Button type="button" onClick={handleSubmit} disabled={!canSubmit} data-testid="pb-submit">
            {submitting ? '...' : (isEdit ? t('common.save', 'Save') : t('banner.adminPublish', 'Publish banner'))}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default AdminBannerFormModal;
